import { useState, useEffect } from "react";
import api from "../../../Services/api";
import Swal from "sweetalert2";
import "./cadastroSecaoModal.css";

const CadastroSecaoModal = ({
    open,
    onClose,
    IDSecao,
    IDCliente,
    totalSecoes = 0,
    onSaved,
    clienteNome,
    clienteUf,
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        IDSecao: 0,
        IDCliente: IDCliente,
        descricao: "",
        ordem: 1,
        situacao: 1,
        dataCadastro: "",
        dataAlteracao: "",
    });

    const calcularProximaOrdem = (lista = []) => {
        const usadas = new Set();
        let max = 0;
        lista.forEach((item) => {
            const ordem = Number(item?.ordem);
            if (Number.isFinite(ordem) && ordem > 0) {
                usadas.add(ordem);
                if (ordem > max) max = ordem;
            }
        });
        for (let i = 1; i <= max + 1; i += 1) {
            if (!usadas.has(i)) return i;
        }
        return max + 1;
    };

    // Carregar dados ao abrir o modal
    useEffect(() => {
        if (!open) return;

        const carregar = async () => {
            if (!IDSecao) {
                setLoading(true);
                setForm({
                    IDSecao: 0,
                    IDCliente: IDCliente,
                    descricao: "",
                    ordem: 1,
                    situacao: 1,
                    dataCadastro: "",
                    dataAlteracao: "",
                });
                try {
                    if (IDCliente && totalSecoes > 0) {
                        const response = await api.get("/chksecoesstats", {
                            params: {
                                page: 1,
                                limit: totalSecoes,
                                IDCliente,
                                filter: "",
                            },
                        });
                        const lista = response.data?.data ?? [];
                        const proximaOrdem = calcularProximaOrdem(lista);
                        setForm((prev) => ({ ...prev, ordem: proximaOrdem }));
                    }
                } catch (err) {
                    console.error("Erro ao buscar próxima ordem", err);
                } finally {
                    setLoading(false);
                }
                return;
            }

            try {
                const resp = await api.get(`/chksecao/${IDSecao}`);

                setForm({
                    IDSecao: resp.data.IDSecao,
                    IDCliente: resp.data.IDCliente,
                    descricao: resp.data.descricao,
                    ordem: resp.data.ordem ?? 1,
                    situacao: resp.data.situacao ?? 1,
                    dataCadastro: resp.data.dataCadastro,
                    dataAlteracao: resp.data.dataAlteracao
                });

            } catch (err) {
                console.error(err);
                Swal.fire("Erro", "Erro ao carregar a seção.", "error");
            }

            setLoading(false);
        };

        carregar();
    }, [IDSecao, open, IDCliente, totalSecoes]);

    // Atualizar inputs
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Salvar
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const payload = {
            IDSecao: IDSecao ?? form.IDSecao,
            IDCliente: form.IDCliente,
            descricao: form.descricao.trim(),
            ordem: form.ordem ?? null,
            situacao: form.situacao ?? 1
        };

        try {
            if (IDSecao) {
                await api.put(`/chksecao/${IDSecao}`, payload);

                await Swal.fire({
                    icon: "success",
                    title: "Atualizado!",
                    text: "Seção atualizada com sucesso.",
                });
            } else {
                await api.post(`/chksecao`, payload);

                await Swal.fire({
                    icon: "success",
                    title: "Cadastrado!",
                    text: "Seção cadastrada com sucesso.",
                });
            }

            onSaved(); // sinaliza para recarregar a lista
            onClose(); // fecha o modal

        } catch (err) {
            console.error(err);
            Swal.fire("Erro", "Erro ao salvar os dados.", "error");
        }

        setSaving(false);
    };

    if (!open) return null;

    return (
        <div className="cadSec-modal-overlay">
            <div className="cadSec-modal">

                <h2 className="cadSec-title">
                    {IDSecao ? "Editar Seção" : "Cadastrar Nova Seção"}
                </h2>

                {loading ? (
                    <div className="cadSec-loading">Carregando...</div>
                ) : (
                    <form className="cadSec-form" onSubmit={handleSubmit}>

                        {/* Linha 1 */}
                        <div className="cadSec-row">

                            {IDSecao && (
                                <div className="cadSec-field">
                                    <label>ID Seção</label>
                                    <input type="text" value={form.IDSecao} disabled />
                                </div>
                            )}

                            <div className="cadSec-field">
                                <label>Cliente</label>
                                <input
                                    type="text"
                                    value={
                                        clienteNome
                                            ? `${clienteNome}${clienteUf ? ` - ${clienteUf}` : ""}`
                                            : `ID ${form.IDCliente}`
                                    }
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Linha 2 */}
                        <div className="cadSec-field">
                            <label>Descrição</label>
                            <input
                                type="text"
                                name="descricao"
                                maxLength={150}
                                minLength={3}
                                value={form.descricao}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {/* Linha 3 */}
                        <div className="cadSec-row">
                            <div className="cadSec-field cadSec-field-ordem">
                                <label>Ordem<span style={{ fontSize: 10, paddingLeft: 5 }}>(Opcional)</span></label>
                                <input
                                    type="number"
                                    name="ordem"
                                    min={1}
                                    step={1}
                                    value={form.ordem}
                                    onChange={handleChange}
                                    onBlur={() => setForm(prev => ({ ...prev, ordem: prev.ordem || 1 }))}
                                    placeholder="Opcional"
                                />
                            </div>

                            {IDSecao && (
                                <div className="cadSec-field cadSec-field-situacao">
                                    <label>Situacao</label>
                                    <select
                                        name="situacao"
                                        value={form.situacao ?? 1}
                                        onChange={handleChange}
                                    >
                                        <option value={2} disabled>Finalizado</option>
                                        <option value={1}>Ativo</option>
                                        <option value={0}>Desativado</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Linha 4 - Datas */}
                        {IDSecao && (
                            <div className="cadSec-row">
                                <div className="cadSec-field">
                                    <label>Cadastrado em</label>
                                    <input type="text" value={new Date(form.dataCadastro).toLocaleString('pt-BR', {
                                        dateStyle: 'short',
                                        timeStyle: 'short'
                                    })} disabled />
                                </div>

                                <div className="cadSec-field">
                                    <label>Alterado em</label>
                                    <input type="text" value={new Date(form.dataAlteracao).toLocaleString('pt-BR', {
                                        dateStyle: 'short',
                                        timeStyle: 'short'
                                    })} disabled />
                                </div>
                            </div>
                        )}

                        <div className="cadSec-buttons">
                            <button type="button" className="cadSec-btn-cancel" onClick={onClose}>
                                Cancelar
                            </button>

                            <button type="submit" className="cadSec-btn-save" disabled={saving}>
                                {saving ? "Salvando..." : "Salvar"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CadastroSecaoModal;
